import {
    AsyncStatus,
    isNullEmptyOrUndefined,
    useApiWorker,
    useDownloadingToast,
    useSafeStateUpdate,
    useToasts,
  } from '@techsharegov/react-ui';
  import { RouteState, useRouteStore } from 'Context/RouteStore';
  import axios, { CancelTokenSource } from 'axios';
  import download from 'downloadjs';
  import { useCallback, useEffect, useMemo, useState } from 'react';

  export const DEFAULT_TITLE = '';
  
  export const useFileDownloader = () => {
    const API = useApiWorker();
    const toast = useToasts();
    const [showLoadingToast, hideLoadingToast] = useDownloadingToast();
  
    const downloadFile = async (
      endpoint: string,
      objectName: string,
      forceDownload?: boolean,
      onSuccess?: (response: any) => void,
      onError?: (response: any) => void
    ) => {
      showLoadingToast(`Downloading ${objectName}, please wait...`);
      try {
        const { data, headers } = await API.get(endpoint, {
          responseType: 'blob',
        });
  
        const mimeType = headers['content-type'];
        if (mimeType === 'application/pdf' && !forceDownload) {
          var fileURL = URL.createObjectURL(data);
          window.open(fileURL, objectName);
        } else {
          download(data, objectName, mimeType);
        }
        onSuccess && onSuccess(data);
      } catch (err) {
        toast.error(
          err
            ? (err as any).ErrorMessage
            : `Error: Unable to download ${objectName}`
        );
        console.log(err);
        onError && onError(err);
      }
      hideLoadingToast();
    };
  
    return { downloadFile };
  };
  
export function useBlobFetcher(url: string, condition = true) {
  const API = useApiWorker();
  const [fetchStatus, setFetchStatus] = useState(AsyncStatus.Idle);
  const [data, setData] = useState<Blob | undefined>();
  const safeStateUpdate = useSafeStateUpdate();

  const getData = useCallback(
    async (cancelTokenSource?: CancelTokenSource) => {
      safeStateUpdate(() => setFetchStatus(AsyncStatus.Pending));

      try {
        const { data: blobData, headers } = await API.get(url, {
          cancelToken: cancelTokenSource?.token,
          responseType: 'blob',
        });

        if (blobData !== undefined) {
          safeStateUpdate(() => setData(blobData));

          safeStateUpdate(() => setFetchStatus(AsyncStatus.Success));

          return blobData;
        } else {
          safeStateUpdate(() => {
            setFetchStatus(AsyncStatus.Error);
          });

          return null;
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          safeStateUpdate(() => setFetchStatus(AsyncStatus.Cancelled));
        }

        safeStateUpdate(() => {
          setFetchStatus(AsyncStatus.Error);
        });

        return null;
      }
    },
    [url, condition]
  );

  useEffect(() => {
    let cancelTokenSource = axios.CancelToken.source();
    if (condition) {
      getData(cancelTokenSource);
    } else {
      setFetchStatus(AsyncStatus.Idle);
    }

    return () => cancelTokenSource.cancel();
  }, [url, condition, getData]);

  return [data, fetchStatus, { getData }] as const;
}

export const useDocumentTitle = (
  subTitle: string,
  nestRoutes = true,
  clearOnDismount = false
) => {
  const routeLabels = useRouteStore((state: RouteState) => state.routeLabels);
  const addRoute = useRouteStore((state: RouteState) => state.addRoute);
  const removeRoute = useRouteStore((state: RouteState) => state.removeRoute);
  const clearRoutes = useRouteStore((state: RouteState) => state.clearRoutes);
  const fullTitle = useMemo(() => {
    let title = nestRoutes ? routeLabels.join(' - ') : subTitle;
    return [DEFAULT_TITLE, title]
      .filter((v) => !isNullEmptyOrUndefined(v))
      .join(' - ');
  }, [subTitle, routeLabels, nestRoutes]);

  useEffect(() => {
    if (nestRoutes && !isNullEmptyOrUndefined(subTitle)) {
      addRoute(subTitle);
    }

    return () => {
      clearOnDismount ? clearRoutes() : removeRoute(subTitle);
    };
  }, [subTitle]);

  return fullTitle;
};
